import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	// Divider,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppSelector } from "@store/hooks";
import type React from "react";
import { /* useCallback, */ useEffect, useRef, useState } from "react";
import {
	publishCurrentState,
	setupFive9Callbacks,
} from "./services/five9Integration";

// Styled components for the compact status display
const StatusContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: "15px",
	padding: "6px 6px",
	background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
	border: "2px solid #e9ecef",
	borderRadius: "25px",
	boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
	fontWeight: 500,
	minWidth: "400px",
	maxWidth: "600px",
	transition: "all 0.3s ease",
	marginRight: "16px",
	fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
	cursor: "pointer",
	"&:hover": {
		boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
		transform: "translateY(-1px)",
	},
}));

const StatusText = styled(Typography)({
	fontSize: "16px",
	color: "#495057",
	flex: 1,
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
});

const DurationChip = styled(Chip)({
	fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
	fontSize: "14px",
	fontWeight: 600,
	color: "#6c757d",
	backgroundColor: "#f8f9fa",
	border: "1px solid #dee2e6",
	minWidth: "48px",
	height: "28px",
});

// Urgency-based styling with animations
const getUrgencyStyles = (urgency: string) => {
	switch (urgency) {
		case "high":
			return {
				borderColor: "#dc3545",
				background: "linear-gradient(135deg, #fff5f5 0%, #ffe6e6 100%)",
				boxShadow: "0 4px 15px rgba(220,53,69,0.2)",
				animation: "pulse 2s infinite",
				"& .status-text": { color: "#dc3545", fontWeight: 600 },
				"& .MuiChip-root": { backgroundColor: "#dc3545", color: "white" },
				"@keyframes pulse": {
					"0%, 100%": { boxShadow: "0 4px 15px rgba(0,0,0,0.1)" },
					"50%": { boxShadow: "0 4px 20px rgba(220,53,69,0.3)" },
				},
			};
		case "medium":
			return {
				borderColor: "#ffc107",
				background: "linear-gradient(135deg, #fffbf0 0%, #fef3c7 100%)",
				boxShadow: "0 4px 15px rgba(255,193,7,0.2)",
			};
		case "normal":
			return {
				borderColor: "#28a745",
				background: "linear-gradient(135deg, #f0fff4 0%, #e6fff0 100%)",
				boxShadow: "0 4px 15px rgba(40,167,69,0.2)",
				"& .status-text": { color: "#28a745", fontWeight: 600 },
				"& .MuiChip-root": { backgroundColor: "#28a745", color: "white" },
			};
		default:
			return {};
	}
};

const UserInfoCard = styled(Paper)(({ theme }) => ({
	background: "linear-gradient(135deg, #28a745, #20c997)",
	color: "white",
	padding: "16px",
	marginBottom: "16px",
	textAlign: "center",
}));

type Five9CompactStatusProps = {};

interface Five9StatusData {
	status: string;
	priority: number;
	urgency: string;
	duration: string;
	timestamp: number;
}

interface Five9LoginForm {
	username: string;
	password: string;
}

interface Five9UserInfo {
	userId?: string;
	username?: string;
	email?: string;
	orgId?: string;
	farmId?: string;
}

const Five9CompactStatus: React.FC<Five9CompactStatusProps> = () => {
	const rightioUserId = useAppSelector(({ state }) => state.UserId);
	const [five9StatusData, setFive9StatusData] =
		useState<Five9StatusData | null>(null);
	const [currentDuration, setCurrentDuration] = useState("00:00");
	const [menuOpen, setMenuOpen] = useState(false);
	const [userInfo, setUserInfo] = useState<Five9UserInfo | null>(null);
	const [loginForm, setLoginForm] = useState<Five9LoginForm>({
		username: "",
		password: "",
	});
	const [statusMessage, setStatusMessage] = useState<{
		type: "success" | "error" | "info" | "warning";
		text: string;
	} | null>(null);
	const [isConnecting, setIsConnecting] = useState(false);
	const usernameRef = useRef<HTMLInputElement>(null);
	const connectionInProgressRef = useRef<boolean>(false);

	// Subscribe to Five9 status updates via pub/sub system
	useEffect(() => {
		const setupStatusSubscription = () => {
			if (window.five9Tester?.subscribeToStatus) {
				console.log("💓 [FIVE9_COMPACT] Subscribing to status heartbeat...");

				window.five9Tester.subscribeToStatus("ReactCompactStatus", (data) => {
					// console.log("💓 [FIVE9_COMPACT] Heartbeat update:", data);
					setFive9StatusData(data);
					setCurrentDuration(data.duration);
				});

				console.log("✅ [FIVE9_COMPACT] Status subscription registered");
			}
		};

		// If Five9 service is available, set up immediately
		if (window.five9Tester) {
			setupStatusSubscription();
		} else {
			// Wait for Five9 service to be available
			const checkInterval = setInterval(() => {
				if (window.five9Tester) {
					setupStatusSubscription();
					clearInterval(checkInterval);
				}
			}, 100);

			// Clear interval after 5 seconds if not found
			setTimeout(() => {
				clearInterval(checkInterval);
			}, 5000);
		}

		// Cleanup subscription on unmount
		return () => {
			if (window.five9Tester?.unsubscribeFromStatus) {
				window.five9Tester.unsubscribeFromStatus("ReactCompactStatus");
				console.log("💓 [FIVE9_COMPACT] Status subscription removed");
			}
		};
	}, []);

	// Subscribe to Five9 user info updates via pub/sub system
	useEffect(() => {
		const setupUserInfoSubscription = () => {
			if (window.five9Tester?.subscribeToUserInfo) {
				console.log("👤 [FIVE9_COMPACT] Subscribing to user info updates...");

				window.five9Tester.subscribeToUserInfo(
					"ReactCompactStatus",
					(userInfo) => {
						console.log("👤 [FIVE9_COMPACT] User info update:", userInfo);

						if (userInfo.isAuthenticated) {
							setUserInfo({
								userId: userInfo.userId || undefined,
								username: userInfo.username || undefined,
								email: userInfo.email || undefined,
								orgId: userInfo.orgId || undefined,
								farmId: userInfo.farmId || undefined,
							});
						} else {
							setUserInfo(null);
						}
					},
				);

				console.log("✅ [FIVE9_COMPACT] User info subscription registered");
			}
		};

		// If Five9 service is available, set up immediately
		if (window.five9Tester) {
			setupUserInfoSubscription();
		} else {
			// Wait for Five9 service to be available
			const checkInterval = setInterval(() => {
				if (window.five9Tester) {
					setupUserInfoSubscription();
					clearInterval(checkInterval);
				}
			}, 100);

			// Clear interval after 5 seconds if not found
			setTimeout(() => {
				clearInterval(checkInterval);
			}, 5000);
		}

		// Cleanup subscription on unmount
		return () => {
			if (window.five9Tester?.unsubscribeFromUserInfo) {
				window.five9Tester.unsubscribeFromUserInfo("ReactCompactStatus");
				console.log("👤 [FIVE9_COMPACT] User info subscription removed");
			}
		};
	}, []);

	// No need to manage credentials - Five9AuthService handles this automatically

	// Auto-focus username field when menu opens
	useEffect(() => {
		if (menuOpen && !userInfo) {
			setTimeout(() => {
				usernameRef.current?.focus();
			}, 100);
		}
	}, [menuOpen, userInfo]);

	// Auto-login is handled in Five9State component to prevent duplicates

	// Determine status display and urgency from Five9 data
	const getStatusInfo = () => {
		// If we haven't received Five9 status data yet
		if (!five9StatusData) {
			// Check if Five9 service is available but not connected
			if (window.five9Tester && !userInfo) {
				return {
					status: "🚫 Not Logged In",
					urgency: "high",
					priority: 1,
				};
			}

			return {
				status: "🔄 Initializing Five9...",
				urgency: "medium",
				priority: 2,
			};
		}

		// Use the exact status from Five9 service
		return {
			status: five9StatusData.status,
			urgency: five9StatusData.urgency,
			priority: five9StatusData.priority,
		};
	};

	const handleConnect = async () => {
		// Prevent double authentication attempts
		if (connectionInProgressRef.current || isConnecting) {
			console.warn(
				"🚫 [FIVE9] Authentication already in progress, ignoring duplicate request",
			);
			return;
		}

		if (!loginForm.username || !loginForm.password) {
			setStatusMessage({
				type: "error",
				text: "Please enter username and password",
			});
			return;
		}

		connectionInProgressRef.current = true;
		setIsConnecting(true);
		setStatusMessage({ type: "info", text: "Connecting to Five9..." });

		try {
			// Use the existing Five9AuthService for authentication
			if (window.five9Tester?.authService) {
				console.log(
					"🔐 [FIVE9_COMPACT] Using Five9AuthService for authentication...",
				);

				// Set user-specific prefix for localStorage
				if (rightioUserId && rightioUserId > 0) {
					window.five9Tester.authService.setUserPrefix(
						`rightio_user_${rightioUserId}`,
					);
					console.log(
						`🔐 [FIVE9_COMPACT] Set user prefix: rightio_user_${rightioUserId}`,
					);
				}

				// Set credentials from form
				window.five9Tester.authService.setCredentials(
					loginForm.username,
					loginForm.password,
				);

				// Authenticate with the provided credentials
				const session = await window.five9Tester.authService.authenticate();

				if (session) {
					console.log(
						"✅ [FIVE9_COMPACT] Authentication successful, starting station status verification...",
					);

					// Call station status verification with 2-minute retry after manual authentication
					if (
						window.five9Tester &&
						typeof window.five9Tester.fetchAgentPresence === "function"
					) {
						setStatusMessage({
							type: "info",
							text: "Synchronizing with Five9 station...",
						});

						console.log(
							"🔄 [FIVE9_COMPACT] Starting station status verification with 2-minute retry after manual authentication...",
						);

						try {
							const result = await window.five9Tester.fetchAgentPresence();

							if (result && result.success) {
								console.log(
									`✅ [FIVE9_COMPACT] Station synchronization successful after manual authentication (${result.attempts} attempts)`,
								);

								// CRITICAL: Add missing WebSocket connection and monitoring setup
								console.log(
									"🔄 [FIVE9_COMPACT] Starting WebSocket connection and callback setup after successful manual login...",
								);

								try {
									// 1. Establish WebSocket connection if not already connected
									if (!window.five9Tester.isConnected) {
										console.log(
											"🔄 [FIVE9_COMPACT] Establishing WebSocket connection...",
										);
										await window.five9Tester.connect();
										console.log(
											"✅ [FIVE9_COMPACT] WebSocket connection established",
										);
									} else {
										console.log(
											"✅ [FIVE9_COMPACT] WebSocket already connected",
										);
									}

									// 2. Set up Five9 callbacks for event monitoring
									console.log(
										"🔄 [FIVE9_COMPACT] Setting up Five9 callbacks...",
									);
									setupFive9Callbacks(window.five9Tester);
									console.log(
										"✅ [FIVE9_COMPACT] Five9 callbacks set up successfully",
									);

									// 3. Publish current state immediately
									console.log(
										"🔄 [FIVE9_COMPACT] Publishing current Five9 state...",
									);
									publishCurrentState(window.five9Tester);
									console.log("✅ [FIVE9_COMPACT] Current state published");

									setStatusMessage({
										type: "success",
										text: `Successfully connected to Five9! Station ready after ${result.attempts} attempts. WebSocket monitoring active.`,
									});
								} catch (wsError) {
									console.error(
										"❌ [FIVE9_COMPACT] Failed to establish WebSocket connection or callbacks:",
										wsError,
									);
									setStatusMessage({
										type: "warning",
										text: `Five9 authenticated but WebSocket setup failed: ${wsError instanceof Error ? wsError.message : "Unknown error"}`,
									});
								}
							} else {
								console.error(
									`❌ [FIVE9_COMPACT] Station synchronization failed after manual authentication: ${result?.reason || "Unknown error"}`,
								);

								setStatusMessage({
									type: "warning",
									text: `Connected to Five9 but station sync failed: ${result?.reason || "Unknown error"}`,
								});
							}
						} catch (error) {
							console.error(
								"❌ [FIVE9_COMPACT] Station status verification threw an error after manual authentication:",
								error,
							);

							setStatusMessage({
								type: "warning",
								text: "Connected to Five9 but station verification failed",
							});
						}
					} else {
						// Fallback if fetchAgentPresence not available - still need WebSocket setup
						console.log(
							"⚠️ [FIVE9_COMPACT] fetchAgentPresence not available, proceeding with WebSocket setup...",
						);

						try {
							// 1. Establish WebSocket connection if not already connected
							if (!window.five9Tester.isConnected) {
								console.log(
									"🔄 [FIVE9_COMPACT] Establishing WebSocket connection (fallback)...",
								);
								await window.five9Tester.connect();
								console.log(
									"✅ [FIVE9_COMPACT] WebSocket connection established (fallback)",
								);
							} else {
								console.log(
									"✅ [FIVE9_COMPACT] WebSocket already connected (fallback)",
								);
							}

							// 2. Set up Five9 callbacks for event monitoring
							console.log(
								"🔄 [FIVE9_COMPACT] Setting up Five9 callbacks (fallback)...",
							);
							setupFive9Callbacks(window.five9Tester);
							console.log(
								"✅ [FIVE9_COMPACT] Five9 callbacks set up successfully (fallback)",
							);

							// 3. Publish current state immediately
							console.log(
								"🔄 [FIVE9_COMPACT] Publishing current Five9 state (fallback)...",
							);
							publishCurrentState(window.five9Tester);
							console.log(
								"✅ [FIVE9_COMPACT] Current state published (fallback)",
							);

							setStatusMessage({
								type: "success",
								text: "Successfully connected to Five9! WebSocket monitoring active.",
							});
						} catch (wsError) {
							console.error(
								"❌ [FIVE9_COMPACT] Failed to establish WebSocket connection (fallback):",
								wsError,
							);
							setStatusMessage({
								type: "warning",
								text: `Five9 authenticated but WebSocket setup failed: ${wsError instanceof Error ? wsError.message : "Unknown error"}`,
							});
						}
					}

					setTimeout(() => {
						setMenuOpen(false);
						setStatusMessage(null);
					}, 3000); // Extended to 3 seconds to show station sync result
				} else {
					throw new Error("Authentication failed");
				}
			} else {
				throw new Error("Five9 service not available");
			}
		} catch (error) {
			console.error("❌ [FIVE9_COMPACT] Authentication failed:", error);
			setStatusMessage({
				type: "error",
				text: `Connection failed: ${error instanceof Error ? error.message : "Unknown error"}`,
			});
		} finally {
			connectionInProgressRef.current = false;
			setIsConnecting(false);
		}
	};

	const handleDisconnect = () => {
		// Clear session and localStorage to prevent auto-login
		if (window.five9Tester?.authService) {
			console.log("🧹 [FIVE9_COMPACT] Clearing Five9 session and localStorage");
			window.five9Tester.authService.clearSession();
		}

		// Disconnect WebSocket
		if (window.five9Tester) {
			window.five9Tester.disconnect();
		}

		// Clear form data
		setLoginForm({ username: "", password: "" });

		setStatusMessage({ type: "info", text: "Disconnected from Five9" });
		setTimeout(() => {
			setMenuOpen(false);
			setStatusMessage(null);
		}, 1500);
	};

	const handleMenuToggle = () => {
		setMenuOpen(!menuOpen);
		setStatusMessage(null);
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && !isConnecting) {
			handleConnect();
		}
	};

	const statusInfo = getStatusInfo();
	const urgencyStyles = getUrgencyStyles(statusInfo.urgency);

	return (
		<>
			<Box display="flex" justifyContent="center" alignItems="center">
				<StatusContainer sx={urgencyStyles} onClick={handleMenuToggle}>
					<StatusText className="status-text">{statusInfo.status}</StatusText>
					<DurationChip
						label={currentDuration}
						size="small"
						variant="outlined"
					/>
				</StatusContainer>
			</Box>

			<Dialog
				open={menuOpen}
				onClose={() => setMenuOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "15px",
						minWidth: "400px",
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box display="flex" alignItems="center" gap={1}>
						<LoginIcon />
						Five9 Authentication
					</Box>
					<IconButton onClick={() => setMenuOpen(false)} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent>
					{statusMessage && (
						<Alert
							severity={statusMessage.type}
							sx={{ mb: 2 }}
							variant="filled"
						>
							{statusMessage.text}
						</Alert>
					)}

					{userInfo && (
						<UserInfoCard elevation={0}>
							<Box
								display="flex"
								alignItems="center"
								justifyContent="center"
								gap={1}
								mb={1}
							>
								<PersonIcon />
								<Typography variant="h6" component="h4">
									{userInfo?.username || userInfo?.email || "Five9 Agent"}
								</Typography>
							</Box>
							<Typography variant="body2" sx={{ opacity: 0.9 }}>
								Connected • Five9 Agent
								{userInfo?.userId && ` • ID: ${userInfo.userId}`}
							</Typography>
							{userInfo?.email && userInfo.email !== userInfo.username && (
								<Typography
									variant="body2"
									sx={{ opacity: 0.8, fontSize: "0.75rem" }}
								>
									📧 {userInfo.email}
								</Typography>
							)}
							{userInfo?.orgId && (
								<Typography
									variant="body2"
									sx={{ opacity: 0.7, fontSize: "0.75rem" }}
								>
									Org: {userInfo.orgId} • Farm: {userInfo.farmId || "N/A"}
								</Typography>
							)}
						</UserInfoCard>
					)}

					{!userInfo ? (
						<Stack spacing={3}>
							<TextField
								ref={usernameRef}
								label="Username"
								placeholder="Your Five9 username"
								fullWidth
								value={loginForm.username}
								onChange={(e) =>
									setLoginForm((prev) => ({
										...prev,
										username: e.target.value.toLowerCase().trim(),
									}))
								}
								onKeyPress={handleKeyPress}
								disabled={isConnecting}
							/>

							<TextField
								label="Password"
								type="password"
								placeholder="Your Five9 password"
								fullWidth
								value={loginForm.password}
								onChange={(e) =>
									setLoginForm((prev) => ({
										...prev,
										password: e.target.value,
									}))
								}
								onKeyPress={handleKeyPress}
								disabled={isConnecting}
							/>

							<Box display="flex" gap={2} justifyContent="center" pt={2}>
								<Button
									variant="contained"
									onClick={handleConnect}
									disabled={isConnecting}
									startIcon={<LoginIcon />}
									sx={{ minWidth: "120px" }}
								>
									{isConnecting ? "Connecting..." : "Connect"}
								</Button>

								<Button
									variant="outlined"
									onClick={() => setMenuOpen(false)}
									disabled={isConnecting}
									sx={{ minWidth: "100px" }}
								>
									Cancel
								</Button>
							</Box>
						</Stack>
					) : (
						<Box display="flex" gap={2} justifyContent="center" pt={2}>
							<Button
								variant="contained"
								color="error"
								onClick={handleDisconnect}
								startIcon={<LogoutIcon />}
								sx={{ minWidth: "120px" }}
							>
								Disconnect
							</Button>

							<Button
								variant="outlined"
								onClick={() => setMenuOpen(false)}
								sx={{ minWidth: "100px" }}
							>
								Close
							</Button>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};

export default Five9CompactStatus;
